import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FieldValues } from 'react-hook-form';
import { useState, useEffect, useRef, useCallback } from 'react';
import queryString from 'query-string';
import moment from 'moment-timezone';
import localForage from 'localforage';
import { useQuery } from '@tanstack/react-query';

import HasAssistanceInline from '../has-assistance/HasAssistanceInline';
import Cancelled from '../cancelled/Cancelled';
import Success from '../../common/assistance/success/success';
import SelectCrane from '../../common/assistance/select-crane/SelectCrane';
import InOurWay from '../../common/assistance/on-our-way/InOurWay';
import { addDataToIndexedDb, getDefaultValuesIndexedDB } from '../../../utils/storage.util';
import { encodeHasAssistanceParams } from '../../../utils/security.utils';
import usePushDataLayerEvent, { filterUserServiceDataToEvents } from '../../../utils/google-analytics.util';
import { logData } from '../../../utils/debug.util';
import { hideLoader, hideModal, setProvider, showLoader, showModal } from '../../../store/slices/generalSlice';
import { RootState } from '../../../store/rootReducer';
import storageService from '../../../services/storage.service';
import { IMetadata } from '../../../models/metadata.model';
import { AssistanceInformation, IAssistance, FaultType } from '../../../models/assistance.model';
import useResumeData from '../../../hooks/useResumeData/useResumeData';
// import useReadyToGo from '../../../hooks/useReadyToGo/useReadyToGo';
import useQueryParams from '../../../hooks/useQueryParams/useQueryParams';
import useManageStorage from '../../../hooks/useManageStorage/useManageStorage';
import useAssistance from '../../../hooks/useAssistance/useAssistance';
import { ContentLayout, FormLayout } from '../../../global-styles/layout';
import { theme } from '../../../global-styles/colors';
import { DESPLEGAR_NO_ARRANCA, MARCADOR_DESARROLLO, MOSTRAR_DESTINO, TIEMPO_BANNER } from '../../../constants/env';
import { DATE_FORMAT, DUMMY_IDENTIFIER, faultTypesMeanings, TIME_FORMAT } from '../../../constants';
import Navbar from '../../../components/navbar/navbar';
import NavButtons from '../../../components/nav-buttons/nav-buttons';
import InfoBanner from '../../../components/info-banner/InfoBanner';
import { ImbeeChatVariables } from '../../../components/imbee-chat/imbee-chat-utils';
import ImbeeChat from '../../../components/imbee-chat/ImbeeChat';
import Resume from '../../../components/common/resume/Resume';
import ModalContainer from '../../../components/common/modal-container/modal-container';
import CardInfo from '../../../components/card-info/CardInfo';
import { icons } from '../../../assets/icons/icons';
import { trackError, trackEvent } from '../../../appInsights';
import { IPolicyResponse } from '../../../api/policy/policy';
import { getAssistanceInformation } from '../../../api/assistance/assistance';
import { UnregisteredParams } from './unregistered-types';
import { UnregisteredStyle } from './unregistered-style';
import { INSIGHTS_STEPS, LAST_REFRESH_STEP, STEPS, useUnregisteredData } from './unregistered-data';
import UnregisteredStep6 from './steps/step-6/Unregistered6';
import UnregisteredStep5b from './steps/step-5/Unregistered5b';
import UnregisteredStep5 from './steps/step-5/Unregistered5';
import UnregisteredStep4 from './steps/step-4/Unregistered4';
import UnregisteredStep2 from './steps/step-2/Unregistered2';
import UnregisteredStep1 from './steps/step-1/Unregistered1';

const Unregistered = () => {
  const { t, i18n } = useTranslation(['common', 'auth', 'assistance']);
  const [step, setStep] = useState(0);
  const [coords, setCoords] = useState<GeolocationCoordinates | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams<UnregisteredParams>();
  const componentDidMount = useRef<boolean>(false);
  const prevStep = useRef<number | null>(null);
  const [fromResume, setFromResume] = useState(false);
  const { writeToStorage, readFromStorage } = useManageStorage();
  const dispatch = useDispatch();
  const modal = useSelector((state: RootState) => state.general.modal);
  const providerCode = useSelector((state: RootState) => state.general.provider);
  const query = useQueryParams();
  const isSmsUrl = !!(params.identifier && params.dateFormatInt && params.entityCode);

  const { pushDataLayerEvent } = usePushDataLayerEvent();
  const [showForm] = useState<boolean>(true);
  const [showReloadModal, setShowReloadModal] = useState<boolean>(false);

  const [previousIntervention, setPreviousIntervention] = useState<string>('');
  const [fechaIntervencionAnterior, setFechaIntervencionAnterior] = useState<Date | null>(null);
  const [infoBannerStep, setInfoBannerStep] = useState<number | null>(null);
  const showInfoBanner = infoBannerStep === step;

  const [chatVariables, setChatVariables] = useState<ImbeeChatVariables>([]);

  // Hook para verificación readyToGo
  // const { readyToGo } = useReadyToGo();

  // Init of forms and data
  const { form1, form2, form4, form5, form6, form7, getDefaultValues } = useUnregisteredData(params);

  // Form values data
  const form1Values = form1.watch();
  const form2Values = form2.watch();
  const form4Values = form4.watch();
  const form5Values = form5.watch();
  const form6Values = form6.watch();

  // Assistance mutation
  const { requestAssistance, requestAuthPolicy, requestAuthPolicyOnDemand, queryClient } = useAssistance({
    authPolicyBody: {
      nif: form1Values.dni,
      licensePlate: form1Values.plate,
      identifier: form1Values.identifier,
      insuranceCompany: form1Values.insuranceCompany,
      phone: form1Values.phone,
      onConfirm: (data: IPolicyResponse) => {
        logData('Policy data received', data);
        confirmDataFirstForm(data);
      },
    },
  });

  const assistanceData = queryClient.getQueryData<AssistanceInformation>(['assitanceInformation']);

  const { data: assistanceInformation, isSuccess: assistanceInformationSuccess } = useQuery({
    queryKey: ['getAssistanceInformation', { identifier: params.identifier, entityCode: params.entityCode }],
    queryFn: () => getAssistanceInformation(params.identifier, params.entityCode, 'Unregistered'),
    enabled: !!(!assistanceData && params.identifier && params.entityCode),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const isPrevalidated = !!(
    assistanceInformationSuccess &&
    assistanceInformation?.asistencia?.dni &&
    assistanceInformation.asistencia.plate
  );

  // Salto directo a WHEN, una sola vez, cuando es URL prevalidada
  const [prevalidatedStepApplied, setPrevalidatedStepApplied] = useState(false);
  if (isPrevalidated && !prevalidatedStepApplied) {
    setPrevalidatedStepApplied(true);
    setStep(STEPS.STEP_WHEN);
  }

  const { refetch: fetchAuthPolicy } = requestAuthPolicy;
  const { refetch: fetchAuthPolicyOnDemand } = requestAuthPolicyOnDemand;

  // Get values from indexedDB
  const { savedData } = useResumeData({
    valuesToFetch: [
      getDefaultValuesIndexedDB('assistanceFormNoLogin', undefined, Object.keys(form1Values)),
      getDefaultValuesIndexedDB('assistanceFormNoLogin', undefined, Object.keys(form2Values)),
      getDefaultValuesIndexedDB('assistanceFormNoLogin', undefined, Object.keys(form4Values)),
      getDefaultValuesIndexedDB('assistanceFormNoLogin', undefined, Object.keys(form5Values)),
    ],
    fetch: modal.name === 'resume',
  });

  // Manejo de la lógica de avance al próximo paso
  const handleStepLogic = (customStep?: number) => {
    // Step logic
    let nextStep = step + 1;

    logData('Is other', form5Values.faultType == FaultType.other);

    if (!MOSTRAR_DESTINO && nextStep === STEPS.STEP_DESTINATION) {
      nextStep++;
    }

    if (MOSTRAR_DESTINO && nextStep === STEPS.STEP_DESTINATION && form5Values.faultType !== FaultType.other) {
      nextStep++;
    }

    logData('handleStepLogic', { step, customStep, nextStep });

    if (prevStep.current && prevStep.current > nextStep) {
      void handleResumeValidation(step + 1, prevStep.current);
    } else {
      setStep(customStep ?? nextStep);
      prevStep.current = null;
      setFromResume(false);
    }
  };

  // Verificación de asistencia anterior
  useEffect(() => {
    if (previousIntervention) {
      logData('Previous intervention found', previousIntervention);
    } else {
      logData('No previous intervention found');
    }
  }, [previousIntervention]);

  // Data form functions
  const confirmDataFirstForm = async (data: IPolicyResponse, customStep?: number) => {
    try {
      dispatch(showLoader({ action: 'confirmDataFirstForm' }));
      const completeName = data.tomador?.split(',');
      form1.setValue('name', completeName[1].trim());
      form1.setValue('surname', completeName[0].trim());
      form1.setValue('policyCode', data.codigoPoliza);
      const identifier = data.identificador || params?.identifier;
      if (identifier) form1.setValue('identifier', identifier);

      setPreviousIntervention(data.intervencionAnterior || '');
      setFechaIntervencionAnterior(data.fechaIntervencion || null);

      if (data.intervencionAnterior && data.enCurso) {
        navigate(`/unregistered/${data.intervencionAnterior}/${identifier}`, { replace: true });
      }

      await addDataToIndexedDb(
        {
          ...form1Values,
          name: completeName[1].trim(),
          surname: completeName[0].trim(),
          policyCode: data.codigoPoliza,
          identifier: identifier,
        },
        'assistanceFormNoLogin',
      );
      if (data.intervencionAnterior) {
        setStep(STEPS.STEP_PREVIOUS_INTERVENTION);
      } else {
        handleStepLogic(customStep);
      }
    } catch {
      if (data.codigoPoliza === '412') {
        // Modal de error específico para el código 412: la póliza requiere confirmación manual
        dispatch(showModal({ name: 'error-confirm' }));
      } else {
        // Modal de error genérico cuando la verificación de póliza no permite continuar
        dispatch(showModal({ name: 'error-no-callmeback' }));
        // Eliminar antes de publicar. Solo para avanzar sin chequeo de póliza.
        //        handleStepLogic(customStep);
      }
    } finally {
      dispatch(hideLoader({ action: 'confirmDataFirstForm' }));
    }
  };

  const confirmCoordinates = async (location: [number, number], customStep?: number) => {
    const { setValue, unregister, getValues } = form4;

    unregister('search');
    setValue('latitude', location[0]);
    setValue('longitude', location[1]);
    handleStepLogic(customStep);

    // Save data in indexedDB
    const data = getValues();
    dispatch(showLoader({ action: 'confirmCoordinates' }));
    await addDataToIndexedDb(data, 'assistanceFormNoLogin');
    setCoords(null);
    dispatch(hideModal());
    dispatch(hideLoader({ action: 'confirmCoordinates' }));
  };

  const handleAssitance = (skipStep?: number) => {
    const form5ActValues = form5.getValues();

    const form6ActValues = MOSTRAR_DESTINO ? form6.getValues() : { latitude: 0, longitude: 0 };

    const newAssistanceData = {
      ...form1.getValues(),
      ...form4.getValues(),
      ...form5ActValues,
      ...form2.getValues(),
      tyreChange: form5Values.tyreChange ?? false,
      latitudeDestination: form6ActValues?.latitude,
      longitudeDestination: form6ActValues?.longitude,
    };

    const { dni: _, plate: __, locationDescription: ___, insuranceCompany: ____, ...body } = newAssistanceData;

    if (body.date && body.date instanceof Date && body.time instanceof Date && body.delayed && body.time) {
      const newDate = moment(body.date)
        .set({ minutes: body.time.getMinutes(), hours: body.time.getHours() })
        .utc(false);
      const isoDate = newDate.toISOString();
      body.date = isoDate;
      delete body.time;
    } else {
      delete body.date;
      delete body.time;
    }

    if (!body.redBatteryLight) delete body.redBatteryLight;

    const selectedCraneCode = queryClient.getQueryData<string>(['selectedCrane', body.identifier]);
    if (selectedCraneCode) body.craneCode = selectedCraneCode;

    requestAssistance.mutate(body as IAssistance, {
      onSuccess: async ({ data }) => {
        dispatch(showLoader({ action: 'handleAssitance' }));
        const bodyToStorage = {
          provider: data.codigoColaborador,
          file: data.expediente,
          intervention: data.intervencion,
          identifier: form1Values.identifier,
          providerName: data.colaborador,
        };
        const metaData: IMetadata = {
          interventionNumber: data.intervencion,
          carIdentification: form1Values.plate,
          name: `${body.name || ''} ${body.surname || ''}`,
          assistanceNumber: data.expediente,
          userIdentification: form1Values.dni,
        };
        storageService.setItem('metaData', metaData);
        await writeToStorage('assitanceInformation', bodyToStorage);

        trackEvent('solicitud-asistencia', { data });

        setStep(step + (skipStep ?? 1));
        dispatch(hideLoader({ action: 'handleAssitance' }));
      },
      onError: (error: any) => {
        trackError(error, { body });
      },
    });
  };

  // Manejo del próximo paso
  const handleNextStep = (customStep?: number) => {
    logData('handleNextStep', { step, customStep });

    // if (step <= STEPS.STEP_WHEN) {
    //   readyToGo(step).then((isReady) => {
    //     if (!isReady) {
    //       navigate('/invalid-data');
    //     }
    //   });
    // }

    // We call it once again due to possibles updates 👇
    const assistanceData = queryClient.getQueryData<AssistanceInformation>(['assitanceInformation']);

    switch (step) {
      case STEPS.STEP_VERIFY_POLICY: // (Y) Verificar póliza
        void form1.handleSubmit(async (data) => {
          if (params.identifier && params.identifier !== DUMMY_IDENTIFIER) await fetchAuthPolicy();
          else await fetchAuthPolicyOnDemand();
          if (data.insuranceCompany) dispatch(setProvider({ provider: data.insuranceCompany }));
        })();
        break;

      case STEPS.STEP_WHEN: // (Y) ¿Cuándo?
        if (form2Values.delayed === false) form2.unregister(['date', 'time']);
        void form2.handleSubmit(async (data) => {
          dispatch(showLoader({ action: 'handleNextStep' }));
          await addDataToIndexedDb(data, 'assistanceFormNoLogin');
          handleStepLogic(customStep);
          dispatch(hideLoader({ action: 'handleNextStep' }));
        })();
        break;

      case STEPS.STEP_SELECT_LOCATION: // (Y) Seleccionar ubicación
        void form4.handleSubmit(() => {
          // Modal para que el usuario confirme visualmente la ubicación del vehículo antes de continuar.
          // Al confirmar, se persisten las coordenadas y se avanza al siguiente paso.
          dispatch(
            showModal({
              name: 'confirm-location',
              onConfirm: (location: [number, number]) => {
                confirmCoordinates(location, customStep);
              },
            }),
          );
        })();
        break;

      case STEPS.STEP_VEHICLE_PROBLEMS: // (Y) Problemas del vehículo
        if (customStep) {
          setStep(customStep);
          break;
        }
        void form5.handleSubmit(async (data) => {
          await addDataToIndexedDb(data, 'assistanceFormNoLogin');
          form6.setValue('step', 1);
          form6.setValue('error', false);
          form6.setValue('latitude', form4Values.latitude ?? 0);
          form6.setValue('longitude', form4Values.longitude ?? 0);
          handleStepLogic(customStep);
        })();
        break;

      case STEPS.STEP_PASSENGERS: // Datos de pasajeros
        if (customStep) {
          setStep(customStep);
          break;
        }
        void form5.handleSubmit(async (data) => {
          form5.setValue('generateMediaTask', false);
          await addDataToIndexedDb(data, 'assistanceFormNoLogin');
          handleStepLogic(customStep);
        })();
        break;

      case STEPS.STEP_DESTINATION: // (N) ¿A dónde llevo el vehículo?
        logData('form6Values in', form6Values);
        if (form6Values.step === 1) {
          if (!form6Values.selectDestination || form6Values.selectDestination === 0) {
            form6.setValue('error', true);
          } else if (form6Values.selectDestination === 1) {
            form6.setValue('step', 2);
            form6.setValue('error', false);
          } else {
            handleStepLogic(customStep);
          }
          logData('form6Values out', form6Values);
          break;
        } else if (form6Values.step === 2) {
          if (!form6Values.latitude || !form6Values.longitude) {
            logData('No coordinates selected');
            form6.setValue('error', true);
          } else {
            logData('Coordinates selected', { latitude: form6Values.latitude, longitude: form6Values.longitude });
            form6.setValue('error', false);
            // Modal para que el usuario confirme la ubicación de destino del vehículo.
            // Al confirmar, se guardan los datos de destino en IndexedDB y se avanza al siguiente paso.
            dispatch(
              showModal({
                name: 'confirm-destination-location',
                onConfirm: () => {
                  logData('form6Values out', form6Values);
                  form6.handleSubmit(async (data) => {
                    dispatch(showLoader({ action: 'handleNextStep' }));
                    await addDataToIndexedDb(data, 'assistanceFormNoLogin');
                    form6.setValue('step', 1);
                    handleStepLogic(customStep);
                    dispatch(hideLoader({ action: 'handleNextStep' }));
                  })();
                },
              }),
            );
          }
          logData('form6Values out', form6Values);
        }
        break;

      case STEPS.STEP_RESUME: // (N) Resumen
        if (customStep) {
          setStep(customStep);
          break;
        }

        void form7.handleSubmit(async () => {
          const externalRoad = form4Values.externalRoad ?? false;
          const adults = form5Values.adults ?? 0;
          const minors = form5Values.minors ?? 0;
          const canGenerateMediaTask = externalRoad && (adults + minors > 2 || minors > 0);
          logData('canGenerateMediaTask', canGenerateMediaTask);
          const delayed = form2.getValues('delayed');
          logData('delayed', delayed);
          const faultType = form5Values.faultType;

          // Validación de si se seleccionó un tipo de avería distinto a Otros y descativar la selección de Destino.
          if (faultType !== FaultType.other) {
            logData('handleResumeData', 'Fault type is not other');
            form6Values.selectDestination = 0;
          }

          // Modal para que el usuario confirme si desea que le envíe un taxi antes de continuar.
          // Visible si está en carretera, hay más de 2 pasajeros o hay algún menor.
          // Al confirmar, se persiste la aceptación o no y se avanza al siguiente paso.
          if (canGenerateMediaTask) {
            dispatch(
              showModal({
                name: 'confirm-media-task',
                onConfirm: async (confirm: boolean) => {
                  logData('confirm-media-task', confirm);
                  form5.setValue('generateMediaTask', confirm);
                  if (delayed) {
                    logData('STAP6-Skipping crane selection due to delayed assistance');
                    handleAssitance(2);
                  } else {
                    logData('STAP6-Proceeding to next step');
                    setStep((prevStep) => prevStep + 1);
                  }
                },
              }),
            );
          } else {
            form5.setValue('generateMediaTask', false);
            if (delayed) {
              logData('STAP6-Skipping crane selection due to delayed assistance');
              handleAssitance(2);
            } else {
              logData('STAP6-Proceeding to next step');
              setStep((prevStep) => prevStep + 1);
            }
          }
          logData('generateMediaTask', form5Values.generateMediaTask);
        })();
        break;

      case STEPS.STEP_SELECT_CRANE: // (Y) Seleccionar grúa
        handleAssitance();
        break;

      case STEPS.STEP_SUCCESS: // (Y) Asistencia solicitada con éxito
        if (!customStep && prevStep) {
          prevStep.current = null;
          setFromResume(false);
        }
        setStep(customStep ?? step + 1);
        break;

      case STEPS.STEP_IN_OUR_WAY: // (Y) Ya vamos de camino
        if (!customStep && prevStep) {
          prevStep.current = null;
          setFromResume(false);
        }
        void resetForms();
        if (!assistanceData?.intervention) {
          navigate('/');
          return;
        }
        navigate(
          `/unregistered/${assistanceData?.intervention}/${
            assistanceData.identifier ?? params.identifier ?? form1Values.identifier
          }`,
          {
            replace: true,
          },
        );
        break;
      default:
        break;
    }
  };

  const handleResumeValidation = async (from: number, to: number) => {
    const forms = [form1, form2, form4, form5, form6];
    const formsToValidate = forms.slice(from, to);

    const validationPromises: Promise<boolean>[] = [];

    for (let i = 0; i <= formsToValidate.length - 1; i++) {
      const promise = formsToValidate[i].trigger();
      validationPromises.push(promise);
    }
    const formValidations = await Promise.all(validationPromises);
    if (formValidations.includes(false)) {
      setStep(formValidations.indexOf(false) + from);
    } else {
      setStep(to);
      prevStep.current = null;
      setFromResume(false);
    }
  };

  const handleEditResumeData = (customStep: number, section?: string) => {
    prevStep.current = Math.max(customStep, step);
    setFromResume(true);
    let queryParam;
    if (customStep === 4 && section) {
      queryParam = {
        step: customStep,
        section,
      };
      const paramsString = queryString.stringify(queryParam);
      navigate(`${location.pathname}?${paramsString}`, { replace: true });
    }
    handleNextStep(customStep);

    dispatch(hideModal());
  };

  // Helper functions to reduce complexity
  const renderClientCard = () => (
    <CardInfo
      title={t('auth:unregistered.resume.client-title')}
      data={[
        {
          title: form1Values.dni,
          description: [form1Values.plate],
        },
      ]}
    />
  );

  // Sección de horario de asistencia
  const renderServiceScheduleCard = (canEdit: boolean) => (
    <CardInfo
      actions={{
        ...(canEdit
          ? {
              edit: () => {
                handleEditResumeData(STEPS.STEP_WHEN);
              },
            }
          : {}),
      }}
      title={t('auth:unregistered.resume.service-schedule')}
      data={[
        {
          title: form2Values.delayed
            ? t('auth:unregistered.resume.vehicle-schedule-later', {
                date: form2Values.date ? moment(form2Values.date).format(DATE_FORMAT) : '',
                time: form2Values.time ? moment(form2Values.time).format(TIME_FORMAT) : '',
              })
            : t('auth:unregistered.resume.vehicle-schedule-now'),
        },
      ]}
    />
  );

  // Sección de ubicación del vehículo
  const renderVehicleUbicationCard = (canEdit: boolean) => (
    <CardInfo
      actions={{
        ...(canEdit
          ? {
              edit: () => {
                handleEditResumeData(STEPS.STEP_SELECT_LOCATION);
              },
            }
          : {}),
      }}
      title={t('auth:unregistered.resume.vehicle-ubication')}
      data={[
        {
          title: t('auth:unregistered.resume.ubication'),
          description: [form4Values.locationDescription],
        },
        {
          title: t('auth:unregistered.resume.pickup-place'),
          description: [
            form4Values.externalRoad
              ? t('auth:unregistered.resume.pickup-place-answers.road')
              : t('auth:unregistered.resume.pickup-place-answers.city'),
          ],
        },
        ...(!form4Values.externalRoad
          ? [
              {
                title: t('auth:unregistered.resume.underground-parking'),
                description: [
                  form4Values.undergroundParking
                    ? t('common:common-text.affirmative-response')
                    : t('common:common-text.negative-response'),
                ],
              },
            ]
          : []),
      ]}
    />
  );

  // Sección de avería del vehículo
  const renderVehicleProblemsCard = (
    canEdit: boolean,
    resumeData: (FieldValues | undefined)[],
    faultType: FaultType,
  ) => (
    <CardInfo
      actions={{
        ...(canEdit
          ? {
              edit: () => {
                handleEditResumeData(STEPS.STEP_VEHICLE_PROBLEMS);
              },
            }
          : {}),
      }}
      title={t('assistance:aa.resume.vehicle-problems')}
      data={[
        {
          title: t(`assistance:aa.resume.fault-type`, {
            fault: faultTypesMeanings(t)?.[faultType],
          }),
          description: [...(faultType === FaultType.other ? [form5Values.otherFaultInformation || ''] : [''])],
        },
        ...(faultType !== FaultType.notStarting || !DESPLEGAR_NO_ARRANCA
          ? []
          : [
              {
                title: '',
                description: [
                  resumeData[3]?.notStartingReason === 0
                    ? t('auth:unregistered.step-4.not-start-stop')
                    : t('auth:unregistered.step-4.not-start-others'),
                ],
              },
            ]),
        ...(faultType !== FaultType.puncture
          ? []
          : [{ title: t('assistance:aa.resume.affected-tyres'), description: [resumeData[3]?.affectedTyres] }]),
        ...(form5Values.faultInformation
          ? [
              {
                title: t('auth:unregistered.step-4.fault-information-title'),
                description: [resumeData[3]?.faultInformation],
              },
            ]
          : []),
      ]}
    />
  );

  // Sección de ocupantes del vehículo
  const renderOccupantsCard = (canEdit: boolean) => (
    <CardInfo
      actions={{
        ...(canEdit
          ? {
              edit: () => {
                handleEditResumeData(STEPS.STEP_PASSENGERS, 'adults');
              },
            }
          : {}),
      }}
      title={t('assistance:aa.resume.occupants')}
      data={[
        {
          title: t('assistance:aa.resume.adults'),
          description: [`${form5Values.adults}`],
        },
        {
          title: t('assistance:aa.resume.minors'),
          description: [`${form5Values.minors ?? 0}`],
        },
        {
          title: t('assistance:aa.resume.special-needs'),
          description: [
            form5Values.passengersWithSpecialNeeds
              ? t('common:common-text.affirmative-response')
              : t('common:common-text.negative-response'),
          ],
        },
        {
          title: t('assistance:aa.resume.special-needs-extra-data'),
          description: [form5Values.passengersInformation || '---'],
        },
      ]}
    />
  );

  // Sección de destino solo se muestra si la configuración lo permite
  const renderVehicleDestinationCard = (canEdit: boolean, faultType: FaultType) => {
    if (MOSTRAR_DESTINO && faultType == FaultType.other)
      return (
        <CardInfo
          actions={{
            ...(canEdit
              ? {
                  edit: () => {
                    handleEditResumeData(STEPS.STEP_DESTINATION);
                  },
                }
              : {}),
          }}
          title={t('auth:unregistered.resume.vehicle-destination')}
          data={[
            {
              title: t('auth:unregistered.resume.destination'),
              description: [form6Values.locationDescription || t('auth:unregistered.resume.no-destination')],
            },
          ]}
        />
      );
    else return null;
  };

  const renderMediaTaskModal = () => {
    return (
      <ModalContainer
        fullScreen
        buttons={[
          {
            className: 'primary',
            onClick: () => {
              if (modal.onConfirm) modal.onConfirm(true);
            },
            label: t('common:label-inputs.affirmative-response'),
            dataCy: 'confirm-media-task-btn',
          },
          {
            className: 'secondary',
            onClick: () => {
              if (modal.onConfirm) modal.onConfirm(false);
            },
            label: t('common:label-inputs.negative-response'),
            dataCy: 'confirm-no-media-task-button',
          },
          {
            className: 'secondary',
            onClick: () => {
              dispatch(hideModal());
            },
            label: t('common:buttons.back-to-resume'),
            dataCy: 'confirm-cancel-media-task-button',
          },
        ]}
        active={modal.name === 'confirm-media-task'}
        onClose={() => {
          dispatch(hideModal());
        }}
        title={t('auth:unregistered.step-4.modal-media-title')}
        text={t('auth:unregistered.step-4.modal-media-text')}
        dataCy="confirm-media-task-modal"
      ></ModalContainer>
    );
  };

  // Función para manejar el renderizado de datos resumidos
  const handleResumeData = (resumeData: (FieldValues | undefined)[] | undefined, canEdit = true) => {
    if (!resumeData) return;

    const faultType = Object.values(FaultType).includes(form5Values.faultType)
      ? form5Values.faultType
      : FaultType.other;

    logData('handleResumeData', resumeData);

    return (
      <>
        {resumeData[0] && renderClientCard()}
        {resumeData[1] && renderServiceScheduleCard(canEdit)}
        {resumeData[2] && renderVehicleUbicationCard(canEdit)}
        {resumeData[3] && (
          <>
            {renderVehicleProblemsCard(canEdit, resumeData, faultType)}
            {renderOccupantsCard(canEdit)}
          </>
        )}
        {resumeData[4] && renderVehicleDestinationCard(canEdit, faultType)}
        {renderMediaTaskModal()}
      </>
    );
  };

  // Renderizado de pasos
  const renderSteps = () => {
    switch (step) {
      case STEPS.STEP_VERIFY_POLICY: // (Y) Verificar póliza
        return assistanceInformation?.estadoEnlace === 5 ? (
          <Cancelled />
        ) : (
          <UnregisteredStep1
            form={form1}
            onConfirm={async () => {
              if (params.identifier && params.identifier !== DUMMY_IDENTIFIER) await fetchAuthPolicy();
              else await fetchAuthPolicyOnDemand();
            }}
          />
        );

      case STEPS.STEP_PREVIOUS_INTERVENTION: // (Y) ¿Tuviste una intervención anterior?
        return (
          <HasAssistanceInline
            intervention={previousIntervention}
            assistanceDate={fechaIntervencionAnterior}
            entityCode={providerCode}
            isChatAllowed={true}
            onNewAssistance={() => setStep(STEPS.STEP_WHEN)}
          />
        );

      case STEPS.STEP_WHEN: // (Y) ¿Cuándo?
        return <UnregisteredStep2 form={form2} fromResume={fromResume} />;

      case STEPS.STEP_SELECT_LOCATION: // (Y) Seleccionar ubicación
        return <UnregisteredStep4 form={form4} coords={coords} />;

      case STEPS.STEP_VEHICLE_PROBLEMS: // (Y) Problemas del vehículo
        return <UnregisteredStep5 form={form5} />;

      case STEPS.STEP_PASSENGERS: // Datos de pasajeros
        return <UnregisteredStep5b form={form5} />;

      case STEPS.STEP_DESTINATION: // (N) ¿A dónde llevo el vehículo?
        return <UnregisteredStep6 form={form6} />;

      case STEPS.STEP_RESUME: // (Y) Resumen
        return (
          <Resume
            resumedData={() =>
              handleResumeData([form1Values, form2Values, form4Values, form5Values, form6Values], true)
            }
            form={form7}
            title={t('auth:unregistered.resume.title')}
            description={t('auth:unregistered.resume.description')}
            canEdit={true}
          />
        );

      case STEPS.STEP_SELECT_CRANE: // (Y) Seleccionar grúa
        return (
          <SelectCrane
            coords={[form4Values.latitude, form4Values.longitude]}
            assistanceInfo={{
              identifier: form1Values.identifier,
              faultType: form5Values.faultType,
              latitude: form4Values.latitude,
              longitude: form4Values.longitude,
            }}
            onNextStep={handleNextStep}
          />
        );

      case STEPS.STEP_SUCCESS: // (Y) Asistencia solicitada con éxito
        return (
          <Success
            isDelayed={form2Values.delayed}
            name={form1Values.name || ''}
            surname={form1Values.surname || ''}
            assistanceInfo={{
              insuranceCompany: params.entityCode ?? '',
              identifier: form1Values.identifier,
            }}
            resetForms={resetForms}
            nextStep={() => handleNextStep()}
            onCheckResume={() => {
              // Modal de resumen accesible desde la pantalla de éxito,
              // permite al usuario revisar los datos de la asistencia solicitada
              dispatch(showModal({ name: 'resume' }));
            }}
          />
        );

      case STEPS.STEP_IN_OUR_WAY: // (Y) En camino
        return (
          <InOurWay
            isDelayed={form2Values.delayed}
            nextStep={() => handleNextStep()}
            assistanceInfo={{
              insuranceCompany: form1Values.insuranceCompany ?? providerCode ?? '',
              identifier: form1Values.identifier,
              intervention: assistanceData?.intervention,
            }}
            resetForms={resetForms}
          />
        );
      default:
        break;
    }
  };

  // Reiniciar todos los formularios
  const resetForms = useCallback(async () => {
    dispatch(showLoader({ action: 'resetForms' }));
    await localForage.removeItem('unregisteredStep');

    queryClient.resetQueries({ queryKey: ['policyData'] });
    queryClient.resetQueries({ queryKey: ['policyDataOnDemand'] });

    setPreviousIntervention('');

    form1.reset(getDefaultValues.form1);
    form2.reset(getDefaultValues.form2);
    form4.reset(getDefaultValues.form4);
    form5.reset(getDefaultValues.form5);
    form6.reset(getDefaultValues.form6);
    form7.reset(getDefaultValues.form7);

    prevStep.current = null;
    setFromResume(false);
    dispatch(hideModal());
    dispatch(hideLoader({ action: 'resetForms' }));
  }, [
    dispatch,
    form1,
    form2,
    form4,
    form5,
    form6,
    form7,
    getDefaultValues.form1,
    getDefaultValues.form2,
    getDefaultValues.form4,
    getDefaultValues.form5,
    getDefaultValues.form6,
    getDefaultValues.form7,
    queryClient,
  ]);

  // Saltar a un paso específico
  type RESETABLE_STEPS = 1 | 2 | 3 | 4 | 5 | 6;
  const resetToSpecificStep = async (step: RESETABLE_STEPS) => {
    logData('resetToSpecificStep', step);
    const steps = [
      { form: form1, defaultValue: getDefaultValues.form1 },
      { form: form2, defaultValue: getDefaultValues.form2 },
      { form: form4, defaultValue: getDefaultValues.form4 },
      { form: form5, defaultValue: getDefaultValues.form5 },
      { form: form6, defaultValue: getDefaultValues.form6 },
    ];

    dispatch(showLoader({ action: 'resetToSpecificStep' }));
    const promises = [];

    const form1Reset: any = form1.reset;
    const form2Reset: any = form2.reset;
    const form4Reset: any = form4.reset;
    const form5Reset: any = form5.reset;
    const form6Reset: any = form6.reset;

    if (step >= STEPS.STEP_WHEN)
      promises.push(getDefaultValuesIndexedDB('assistanceFormNoLogin', form1Reset, Object.keys(form1Values)));
    if (step >= STEPS.STEP_SELECT_LOCATION)
      promises.push(
        getDefaultValuesIndexedDB('assistanceFormNoLogin', form2Reset, Object.keys(form2Values), form2Values),
      );
    if (step >= STEPS.STEP_VEHICLE_PROBLEMS)
      promises.push(getDefaultValuesIndexedDB('assistanceFormNoLogin', form4Reset, Object.keys(form4Values)));
    if (step >= STEPS.STEP_DESTINATION)
      promises.push(getDefaultValuesIndexedDB('assistanceFormNoLogin', form5Reset, Object.keys(form5Values)));
    if (step >= STEPS.STEP_RESUME)
      promises.push(getDefaultValuesIndexedDB('assistanceFormNoLogin', form6Reset, Object.keys(form6Values)));

    const values = await Promise.all(promises);

    const newData = values.reduce((pv, cv) => {
      pv = { ...pv, ...cv };
      return pv;
    }, {});

    // here we set the values according to the selected step
    steps.forEach(({ form: stepForm, defaultValue }, index) => {
      if (values[index]) {
        stepForm.reset(values[index]);
      } else {
        stepForm.reset(defaultValue);
      }
    });

    await localForage.setItem('assistanceFormNoLogin', newData);
    setStep(step);
    readFromStorage();
    dispatch(hideLoader({ action: 'resetToSpecificStep' }));
  };

  // Guardar en localStorage si se muestra el formulario o no
  useEffect(() => {
    storageService.setItem('showForm', showForm ? '1' : '0');
  }, [showForm]);

  // Verificar si ya ha sido creada la intervención o si ya ha finalizado
  useEffect(() => {
    if (!assistanceInformationSuccess || !assistanceInformation) return;

    // ********** Manejo de flujo ********** */

    logData('Y) Manejo de flujo - Assistance information fetched', assistanceInformation);
    logData(`Y) Manejo de flujo - Estado de enlace: ${assistanceInformation.estadoEnlace}`);
    logData(`Y) Manejo de flujo - Estado de asistencia: ${assistanceInformation.estadoAsistencia}`);

    /* Enlace manipulado por el usuario - Envía a la pantalla de error */
    if (assistanceInformation?.estadoEnlace == 9) {
      navigate(`/invalid-data`);
      return;
    }

    /* Enlace caducado - Envía a la pantalla de enlace expirado */
    if (assistanceInformation && [2, 3].includes(assistanceInformation?.estadoEnlace)) {
      navigate('/expired-link');
      return;
    }

    switch (assistanceInformation?.estadoAsistencia) {
      case 1: // Asistencia activa
        if (assistanceInformation?.estadoEnlace == 1 && assistanceInformation?.intervencion && params.identifier) {
          // Estado para el enlace vigente. Continuar a pantalla de seguimiento.
          navigate(`/unregistered/${assistanceInformation?.intervencion}/${params.identifier}`);
          return;
        }
        break;
      case 2: // Asistencia finalizada con menos de 15 días
        const reference = encodeHasAssistanceParams(providerCode!, true);
        navigate(`/has-assistance/${reference}/${assistanceInformation?.intervencion}`);
        return;

      default:
        break;
    }
    // ************************************** */

    // Salto directo a WHEN cuando es URL prevalidada (sin paso de intervención anterior)
    const asistencia = assistanceInformation?.asistencia;
    if (asistencia?.dni && asistencia.plate) {
      form1.reset({ ...asistencia, identifier: params.identifier || '' });
    }
  }, [assistanceInformation, assistanceInformationSuccess, params.identifier, form1, navigate, providerCode]);

  // Configuración inicial del formulario y recuperación de datos indexados
  useEffect(() => {
    if (!componentDidMount.current) {
      i18n.changeLanguage(params.language);

      dispatch(hideModal());
      const getIndexedData = async () => {
        dispatch(showLoader({ action: 'getIndexedData' }));
        const data: FieldValues | null = await localForage.getItem('assistanceFormNoLogin');
        if (params?.identifier && data?.identifier !== params?.identifier) {
          logData('Identifier mismatch. Clearing data.', {
            storedIdentifier: data?.identifier,
            urlIdentifier: params?.identifier,
          });
          void resetForms();
          setStep(0);
          dispatch(hideLoader({ action: 'getIndexedData' }));
          return;
        }

        if (data?.insuranceCompany) {
          dispatch(setProvider(data?.insuranceCompany));
        }

        const form1Reset: any = form1.reset;
        const form2Reset: any = form2.reset;
        const form4Reset: any = form4.reset;
        const form5Reset: any = form5.reset;
        const form6Reset: any = form6.reset;

        const [indexedStep, savedPathname, firstStepValues] = await Promise.all([
          localForage.getItem<number>('unregisteredStep'),
          localForage.getItem<string>('assistanceSavedUrl'),
          getDefaultValuesIndexedDB('assistanceFormNoLogin', form1Reset, Object.keys(form1Values)),
          getDefaultValuesIndexedDB('assistanceFormNoLogin', form2Reset, Object.keys(form2Values), form2Values),
          getDefaultValuesIndexedDB('assistanceFormNoLogin', form4Reset, Object.keys(form4Values)),
          getDefaultValuesIndexedDB('assistanceFormNoLogin', form5Reset, Object.keys(form5Values)),
          getDefaultValuesIndexedDB('assistanceFormNoLogin', form6Reset, Object.keys(form6Values)),
          readFromStorage(),
        ]);

        // If unix date in the params has exceeded 1 hour then reset all the data
        if (
          ((savedPathname && pathname !== savedPathname) ||
            (indexedStep && indexedStep < STEPS.STEP_SUCCESS && indexedStep > STEPS.STEP_WHEN) ||
            !indexedStep) &&
          params?.dateFormatInt
        ) {
          logData('Resetting data', {
            indexedStep,
            savedPathname,
            currentPathname: pathname,
          });
          void resetForms();
          setStep(STEPS.STEP_VERIFY_POLICY);
        } else if (indexedStep && firstStepValues) {
          logData('Setting existing indexed data', { indexedStep, firstStepValues });
          setStep(indexedStep);
          // Si se detectan datos previos en IndexedDB correspondientes a un paso anterior al resumen,
          // se muestra el modal de datos detectados para que el usuario decida si retomar o reiniciar
          if (indexedStep <= STEPS.STEP_RESUME) dispatch(showModal({ name: 'detected-data' }));
        }
        dispatch(hideLoader({ action: 'getIndexedData' }));
      };
      void getIndexedData();
    }
  }, [
    dispatch,
    form1.reset,
    form1Values,
    form2.reset,
    form2Values,
    form4.reset,
    form4Values,
    form5.reset,
    form5Values,
    i18n,
    params.language,
    params?.identifier,
    params?.dateFormatInt,
    pathname,
    resetForms,
    navigate,
    readFromStorage,
    params,
    form6.reset,
    form6Values,
  ]);

  // Almacenar el paso actual en localForage para mantener el estado al recargar la página
  // También se envían variables al chat para mantener el contexto de la asistencia
  useEffect(() => {
    if (componentDidMount.current) {
      void localForage.setItem('unregisteredStep', step);
      window.scrollTo(0, 0);

      const variables = [['pantalla', 'alta']] as ImbeeChatVariables;

      variables.push(['identificador', form1Values.identifier || '']);

      if (step > STEPS.STEP_VERIFY_POLICY || step === STEPS.STEP_PREVIOUS_INTERVENTION) {
        variables.push(['dni', form1Values.dni || '']);
        variables.push(['matricula', form1Values.plate || '']);
        variables.push(['telefono', form1Values.phone || '']);
      }
      if (step > STEPS.STEP_WHEN && step < STEPS.STEP_PREVIOUS_INTERVENTION) {
        variables.push(['diferido', form2Values.delayed || '']);
        if (form2Values.delayed) {
          variables.push(['fecha', form2Values.date || '']);
          variables.push(['hora', form2Values.time || '']);
        }
      }
      if (step > STEPS.STEP_SELECT_LOCATION && step < STEPS.STEP_PREVIOUS_INTERVENTION) {
        variables.push(['latitud', form4Values.latitude || '']);
        variables.push(['longitud', form4Values.longitude || '']);
        variables.push(['direccion', form4Values.locationDescription || '']);
        variables.push(['subterraneo', form4Values.undergroundParking || '']);
        variables.push(['carretera', form4Values.externalRoad || '']);
      }
      if (step > STEPS.STEP_VEHICLE_PROBLEMS && step < STEPS.STEP_PREVIOUS_INTERVENTION) {
        variables.push(['tipoAveria', form5Values.faultType || '']);
        variables.push(['neumaticosAfectados', form5Values.affectedTyres || '']);
        variables.push(['infoAveria', form5Values.faultInformation || '']);
        variables.push(['infoOtra', form5Values.otherFaultInformation || '']);
        variables.push(['pasajerosAdultos', form5Values.adults || '']);
        variables.push(['pasajerosMenores', form5Values.minors || '']);
        variables.push(['pasajerosEspeciales', form5Values.passengersWithSpecialNeeds || '']);
        variables.push(['pasajerosEspecialesInfo', form5Values.passengersInformation || '']);
      }

      if (MARCADOR_DESARROLLO) {
        variables.push(['marcador_desarrollo', 'solo visible si es desarrollo']);
        logData('SERVICE Variables para el chat establecidas (solo visible en desarrollo):', variables);
      }

      setChatVariables(variables);
      logData(`SERVICE Variables para el chat establecidas: ${JSON.stringify(variables)}`);
    } else {
      componentDidMount.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Manejo de cambio de proveedor al ingresar con un código de entidad diferente
  const { entityCode } = params;
  useEffect(() => {
    if (entityCode && providerCode !== entityCode) {
      dispatch(setProvider({ provider: entityCode }));
      storageService.setLocalItem('providerCode', entityCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityCode]);

  // Manejo del banner de información que aparece después de 5 segundos en ciertos pasos
  useEffect(() => {
    const isRelevantStep = step === STEPS.STEP_WHEN || step === STEPS.STEP_VEHICLE_PROBLEMS;
    // const isRelevantStep = step > STEPS.STEP_VERIFY_POLICY && step !== STEPS.STEP_PREVIOUS_INTERVENTION;
    if (!isRelevantStep) return;
    const timer = setTimeout(() => {
      setInfoBannerStep(step);
      logData('Info banner displayed after 5 seconds');
    }, TIEMPO_BANNER * 1000);
    return () => clearTimeout(timer);
  }, [step]);

  // Manejo de salto directo a un paso específico desde el resumen
  useEffect(() => {
    const section = Array.isArray(query?.section) ? query.section[0] : query?.section;
    const stepValue = query?.step !== null && query?.step !== undefined ? Number(query.step) : undefined;
    if (section && stepValue === 4 && Number(step) === stepValue) {
      const scrollableFields = ['blockedTyres', 'adults'];
      if (typeof section === 'string' && scrollableFields.includes(section)) {
        form5.setFocus(section as any);
      }
      navigate(location.pathname, { replace: true });
    }
  }, [query, step, navigate, form5]);

  // Detectar intento de recarga de página
  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      // Solo prevenir la recarga si hay datos del formulario guardados y no estamos en las pantallas finales
      if (step > 0 && step <= LAST_REFRESH_STEP) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };

    // Detectar combinación Ctrl+R, F5, etc.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (step > 0 && step <= LAST_REFRESH_STEP) {
        // F5 o Ctrl+R
        if (event.key === 'F5' || (event.ctrlKey && event.key === 'r')) {
          event.preventDefault();
          setShowReloadModal(true);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [step, t]);

  // Manejo de eventos para analytics
  useEffect(() => {
    if (step > 0) {
      pushDataLayerEvent(
        'siguiente_step',
        filterUserServiceDataToEvents({
          ...form1Values,
          ...form2Values,
          ...form4Values,
          ...form5Values,
        }),
        INSIGHTS_STEPS[step - 1],
      );
    }
    pushDataLayerEvent('screen_view', null, INSIGHTS_STEPS[step]);
    trackEvent(INSIGHTS_STEPS[step], { step: step, form1Values, form2Values, form4Values, form5Values });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Manejo de eventos para analytics
  useEffect(() => {
    pushDataLayerEvent('inicio_flujo', null, 'validacion-datos');
  }, [pushDataLayerEvent]);

  // Función para manejar el paso siguiente, con lógica específica para ciertos pasos
  const handlePrevStep = (prevStep: number) => {
    if (!MOSTRAR_DESTINO && prevStep === STEPS.STEP_DESTINATION) {
      setStep(prevStep - 1);
    } else if (MOSTRAR_DESTINO && prevStep === STEPS.STEP_DESTINATION && form5Values.faultType !== FaultType.other) {
      setStep(prevStep - 1);
    } else {
      setStep(prevStep);
    }
  };

  const showNavbar = step > STEPS.STEP_VERIFY_POLICY && step <= STEPS.STEP_RESUME;
  const showNavButtons = step <= STEPS.STEP_RESUME;

  const isForm4Valid = (): boolean => {
    return (
      !!form4Values.latitude &&
      !!form4Values.longitude &&
      (form4Values.externalRoad === true ||
        (form4Values.externalRoad === false && form4Values.undergroundParking !== undefined))
    );
  };

  const stepFormValid: Partial<Record<number, boolean>> = {
    [STEPS.STEP_VERIFY_POLICY]: form1.formState.isValid,
    [STEPS.STEP_WHEN]: form2.formState.isValid,
    [STEPS.STEP_SELECT_LOCATION]: isForm4Valid(),
    [STEPS.STEP_VEHICLE_PROBLEMS]:
      form5.formState.isValid && (form5Values.faultType !== FaultType.other || !!form5Values.faultInformation?.trim()),
    [STEPS.STEP_PASSENGERS]: form5Values.adults > 0,
    [STEPS.STEP_DESTINATION]: form6.formState.isValid,
    [STEPS.STEP_RESUME]: form7.formState.isValid,
  };
  const isNextDisabled = stepFormValid[step] === false;

  return (
    showForm && (
      <>
        <ContentLayout className={showNavbar ? 'with-navbar' : ''}>
          <UnregisteredStyle>
            {showNavbar && (
              <Navbar
                assistance={step === STEPS.STEP_RESUME}
                clickLeft={() => {
                  if (step === STEPS.STEP_RESUME || step === STEPS.STEP_SELECT_CRANE)
                    // En el resumen o selección de grúa, el botón izquierdo abre el modal de reset
                    // para que el usuario pueda cancelar la solicitud
                    dispatch(showModal({ name: 'reset' }));
                  else {
                    // En cualquier otro paso del formulario, el botón izquierdo muestra
                    // el modal de resumen con los datos introducidos hasta el momento
                    console.log('resume');
                    dispatch(showModal({ name: 'resume' }));
                  }
                }}
                clickRight={() => {
                  // El botón derecho de la barra de navegación siempre abre el modal de reset
                  // para cancelar y reiniciar la solicitud desde el principio
                  dispatch(showModal({ name: 'reset' }));
                }}
                steps={{ totalSteps: 6, step: step + 1 }}
                iconLeft={icons.resumeIcon}
                iconRight={icons.closeIcon}
                isResume={step === STEPS.STEP_RESUME || step === STEPS.STEP_SELECT_CRANE}
                stepStyle="dots"
              />
            )}
            <div className="register-content">{renderSteps()}</div>
            {showNavButtons && (
              <NavButtons
                prevStep={() => handlePrevStep(step - 1)}
                showPrevButton={
                  (step > STEPS.STEP_VERIFY_POLICY && !isPrevalidated) || (step > STEPS.STEP_WHEN && isPrevalidated)
                }
                nextStep={() => handleNextStep()}
                nextText={step === STEPS.STEP_RESUME ? t('common:buttons.conclude') : t('common:buttons.next')}
                disableNext={isNextDisabled}
              />
            )}
            {((step > STEPS.STEP_VERIFY_POLICY && step <= STEPS.STEP_RESUME) ||
              step === STEPS.STEP_PREVIOUS_INTERVENTION) && (
              <ImbeeChat onClick={() => {}} chatVariables={chatVariables} />
            )}
            {showInfoBanner && (
              <InfoBanner
                message={t('assistance:info-banner.chat-help')}
                bottom={180}
                icon={null}
                arrowCorner="bottom-right"
                borderColor={theme.primary.primary70}
                textColor={theme.primary.primary70}
                arrowColor={theme.primary.primary70}
                backgroundColor={theme.primary.primary10}
                onClick={() => setInfoBannerStep(null)}
              />
            )}
          </UnregisteredStyle>
        </ContentLayout>

        {/* MODALS */}

        {/* Modal para resumir la solicitud */}
        <ModalContainer
          fullScreen
          active={modal.name === 'resume'}
          onClose={() => {
            dispatch(hideModal());
          }}
          title={t('auth:unregistered.resume.title')}
          text={step < 7 ? t('auth:unregistered.resume.description') : t('assistance:aia.resume.description')}
          clickLeft={() => {
            dispatch(showModal({ name: 'reset' }));
          }}
          dataCy="resume-form-modal"
          isResume
          showLeftButton={step < 7}
        >
          <FormLayout>{handleResumeData(savedData, step !== 7)}</FormLayout>
        </ModalContainer>

        {/* Modal de confirmación para reiniciar la solicitud de asistencia */}
        <ModalContainer
          buttons={[
            {
              className: 'primary',
              onClick: async () => {
                await resetForms();
                handlePrevStep(STEPS.STEP_VERIFY_POLICY);
              },
              label: t('assistance:modal-reset.primary-button'),
            },
            {
              className: 'secondary',
              onClick: () => {
                dispatch(hideModal());
              },
              label: t('common:buttons.cancel'),
            },
          ]}
          active={modal.name === 'reset'}
          onClose={() => {
            dispatch(hideModal());
          }}
          title={t('assistance:modal-reset.title')}
          text={t('assistance:modal-reset.description')}
          dataCy="reset-form-modal"
        />

        {/* Modal de datos detectados */}
        <ModalContainer
          buttons={[
            {
              className: 'primary',
              onClick: () => {
                dispatch(hideModal());
              },
              label: t('common:data-detected.continue-button'),
            },
            {
              className: 'secondary',
              onClick: () => {
                const handleClick = async () => {
                  if (isSmsUrl) {
                    await resetToSpecificStep(STEPS.STEP_WHEN as RESETABLE_STEPS);
                    dispatch(hideModal());
                    return;
                  }
                  await resetForms();
                  handlePrevStep(STEPS.STEP_VERIFY_POLICY);
                };
                void handleClick();
              },
              label: t('common:data-detected.reset-button'),
            },
          ]}
          active={modal.name === 'detected-data'}
          onClose={() => {
            dispatch(hideModal());
          }}
          title={t('common:data-detected.title')}
          text={t('common:data-detected.description')}
          dataCy="detected-data-form-modal"
        />

        {/* Modal de confirmación de recarga de página */}
        <ModalContainer
          buttons={[
            {
              className: 'primary',
              onClick: () => {
                setShowReloadModal(false);
              },
              label: t('auth:unregistered.confirm-reload.stay-page'),
            },
            {
              className: 'secondary',
              onClick: async () => {
                await resetForms();
                setShowReloadModal(false);
                setStep(STEPS.STEP_VERIFY_POLICY);
              },
              label: t('auth:unregistered.confirm-reload.leave-page'),
            },
          ]}
          active={showReloadModal}
          onClose={() => {
            setShowReloadModal(false);
          }}
          title={t('auth:unregistered.confirm-reload.title')}
          text={t('auth:unregistered.confirm-reload.text')}
          dataCy="reload-page-modal"
        />
      </>
    )
  );
};

export default Unregistered;
